import { RefObject } from 'react';

export interface ScrollToElementOptions {
	/** Whether to retry scrolling if element is not immediately available */
	withRetry?: boolean;
	/** Delay before attempting to scroll (in milliseconds) */
	delay?: number;
	/** Interval between retry attempts (in milliseconds) */
	retryInterval?: number;
	/** Maximum time to keep retrying (in milliseconds) */
	retryTimeout?: number;
	/** Callback function to execute on successful scroll */
	onSuccess?: () => void;
	/** Native ScrollIntoViewOptions for scrollIntoView behavior */
	scrollOptions?: ScrollIntoViewOptions;
}

const DEFAULT_OPTIONS: Required<ScrollToElementOptions> = {
	withRetry: false,
	delay: 0,
	retryInterval: 100,
	retryTimeout: 2000,
	onSuccess: () => {},
	scrollOptions: { behavior: 'smooth', inline: 'center' }
};

/**
 * Scrolls to a specific element with advanced options including retry logic and delays.
 *
 * @param elementRef - React ref object pointing to the target element
 * @param options - Configuration options for scroll behavior
 * @returns Cleanup function to cancel any ongoing timeouts/intervals
 *
 * @example
 * ```tsx
 * const myRef = useRef<HTMLDivElement>(null);
 *
 * // Simple scroll
 * scrollToElement(myRef);
 *
 * // Scroll with retry logic and delay
 * const cleanup = scrollToElement(myRef, {
 *   withRetry: true,
 *   delay: 100,
 *   retryTimeout: 3000,
 *   onSuccess: () => console.log('Scrolled successfully'),
 *   scrollOptions: { behavior: 'smooth', block: 'center' }
 * });
 *
 * // Cleanup if needed (e.g., in useEffect cleanup)
 * return cleanup;
 * ```
 */
export const scrollToElement = (
	elementRef: RefObject<HTMLElement>,
	options: ScrollToElementOptions = {}
): (() => void) | undefined => {
	const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
	const {
		withRetry,
		delay,
		retryInterval,
		retryTimeout,
		onSuccess,
		scrollOptions
	} = mergedOptions;

	const performScroll = (): boolean => {
		if (elementRef.current) {
			elementRef.current.scrollIntoView(scrollOptions);
			onSuccess();
			return true;
		}
		return false;
	};

	const handleRetryScroll = (): (() => void) => {
		if (performScroll()) {
			return () => {};
		}

		const retryIntervalId = setInterval(() => {
			if (performScroll()) {
				clearInterval(retryIntervalId);
			}
		}, retryInterval);

		const timeoutId = setTimeout(() => {
			clearInterval(retryIntervalId);
		}, retryTimeout);

		return () => {
			clearInterval(retryIntervalId);
			clearTimeout(timeoutId);
		};
	};

	if (delay > 0) {
		const timeoutId = setTimeout(() => {
			if (withRetry) {
				handleRetryScroll();
			}
			else {
				performScroll();
			}
		}, delay);
		return () => clearTimeout(timeoutId);
	}

	if (withRetry) {
		return handleRetryScroll();
	}

	performScroll();
	return undefined;
};